/**
 * Dashboard Layout
 *
 * Layout for authenticated user pages with sidebar navigation
 * Used for all /dashboard/* routes
 */

import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Header from "../components/Header";
import DashboardSidebar from "../components/dashboard/DashboardSidebar";
import { useOwner } from "../context/OwnerContext";
import { useFullscreen, useBreadcrumb } from "../contexts";
import { DebugToggle } from "../debug";
import "./DashboardLayout.scss";

/**
 * `fullBleed` (28 Sep 2026): for a page that IS its view — the map — no white card and no
 * padding; it fills the space under the header and beside the sidebar. Opt-in, so every
 * other page is unchanged.
 */
const DashboardLayout = ({ children, fullBleed = false }) => {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(() => {
    const saved = localStorage.getItem("dashboardSidebarPinned");
    return saved ? JSON.parse(saved) : true;
  });
  const location = useLocation();
  const { owner } = useOwner();
  const { isFullscreen } = useFullscreen();
  const { overrides } = useBreadcrumb();

  // Sync with localStorage changes from sidebar
  useEffect(() => {
    const handleStorage = () => {
      const saved = localStorage.getItem("dashboardSidebarPinned");
      if (saved) setIsPinned(JSON.parse(saved));
    };
    window.addEventListener("storage", handleStorage);

    // Also check on interval for same-window updates
    const interval = setInterval(() => {
      const saved = localStorage.getItem("dashboardSidebarPinned");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed !== isPinned) setIsPinned(parsed);
      }
    }, 100);

    return () => {
      window.removeEventListener("storage", handleStorage);
      clearInterval(interval);
    };
  }, [isPinned]);

  // Determine sidebar collapsed state for main content margin
  // Collapses when unpinned (regardless of screen size)
  const sidebarCollapsed = !isPinned;

  // Generate breadcrumbs from path
  const getBreadcrumbs = () => {
    const paths = location.pathname.split("/").filter(Boolean);
    const breadcrumbs = [{ label: "Home", path: "/dashboard" }];

    let currentPath = "";
    paths.forEach((segment) => {
      currentPath += `/${segment}`;
      // Skip "dashboard" as first segment since we already have Home
      if (segment === "dashboard" && currentPath === "/dashboard") return;

      // Check for override first, otherwise generate label from segment
      const label = overrides[segment] || segment
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      breadcrumbs.push({ label, path: currentPath });
    });

    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  // A full-bleed page sits EXACTLY under the fixed header, whose height is whatever its
  // content makes it (62px today). Measured, not guessed: the usual 80px offset leaves the
  // breathing room cards want, and a map would show it as a grey strip.
  const layoutRef = useRef(null);
  useLayoutEffect(() => {
    if (!fullBleed || !layoutRef.current) return undefined;
    const root = layoutRef.current;
    const nav = root.querySelector(".custom-navbar");
    if (!nav) return undefined;
    const set = () => root.style.setProperty("--dashboard-header-h", `${nav.getBoundingClientRect().height}px`);
    set();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(set);
    ro.observe(nav);
    return () => ro.disconnect();
  }, [fullBleed, isFullscreen]);

  return (
    <div
      ref={layoutRef}
      className={`dashboard-layout ${isFullscreen ? "fullscreen-mode" : ""}`}
    >
      {!isFullscreen && (
        <>
          <Header
            sidebarCollapsed={sidebarCollapsed}
            breadcrumbs={breadcrumbs}
          />

          <div className="dashboard-layout-body">
            {/* Mobile sidebar overlay */}
            {mobileSidebarOpen && (
              <div
                className="sidebar-overlay"
                onClick={() => setMobileSidebarOpen(false)}
              />
            )}

            {/* Sidebar */}
            <DashboardSidebar
              mobileOpen={mobileSidebarOpen}
              onMobileClose={() => setMobileSidebarOpen(false)}
              owner={owner}
            />

            {/* Main content */}
            <main
              className={`dashboard-main ${
                sidebarCollapsed ? "sidebar-collapsed" : ""
              } ${fullBleed ? "dashboard-main--full-bleed" : ""}`}
            >
              {/* Mobile menu button */}
              <button
                className="mobile-menu-btn"
                onClick={() => setMobileSidebarOpen(true)}
                aria-label="Open menu"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 12H21M3 6H21M3 18H21"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              {/* Page content */}
              <div className={`dashboard-content${fullBleed ? " dashboard-content--full-bleed" : ""}`}>
                {children}
              </div>
            </main>
          </div>
        </>
      )}

      {isFullscreen && (
        <div className={`fullscreen-content${fullBleed ? " fullscreen-content--full-bleed" : ""}`}>
          {children}
        </div>
      )}

      <DebugToggle />
    </div>
  );
};

export default DashboardLayout;
